import dayjs from 'dayjs';
import {
    IDayAvailability,
    IScheduleAvailabilitiesResponse,
    IScheduleAvailabilitiesSingleDateResponse,
    IScheduleAvailabilitiesSingleSlotResponse,
    ISessionConfig
} from '~/types/server.types';

const DAY_MAP = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday'
} as const;

const memoizedData: Record<
    string,
    IScheduleAvailabilitiesSingleSlotResponse[]
> = {};

const generateSlots = (
    date: string
): IScheduleAvailabilitiesSingleSlotResponse[] => {
    const slots: IScheduleAvailabilitiesSingleSlotResponse[] = [];
    const startTime = dayjs(date).hour(9).minute(0); // Start at 9:00 AM

    // 16 half hours between 9 to 17
    for (let i = 0; i < 16; i++) {
        const _startTime = startTime
            .tz('America/Los_Angeles')
            .hour(9 + Math.floor(i / 2))
            .minute(i % 2 === 0 ? 0 : 30)
            .second(0)
            .millisecond(0)
            .toISOString();

        slots.push({
            startTime: _startTime
        });
    }

    return slots;
};

const generateDates = ({
    endDate,
    startDate,
    availableDays
}: {
    startDate: string;
    endDate: string;
    availableDays: IDayAvailability[];
}): IScheduleAvailabilitiesSingleDateResponse[] => {
    const dates: IScheduleAvailabilitiesSingleDateResponse[] = [];
    const _endDate = dayjs(endDate);
    const _startDate = dayjs(startDate);
    const dateDifference = _endDate.diff(startDate, 'day');

    // we want to build map so we can check later if we should generate for current day
    const availableDaysMap = availableDays.reduce(
        (_availableDays, currentDay) => {
            _availableDays[currentDay.day_of_week] = true;
            return _availableDays;
        },
        {}
    );
    for (let i = 0; i <= dateDifference; i++) {
        const currentDay = _startDate.add(i, 'day');
        const currentDayNumber = currentDay.get('day');
        const dayName = DAY_MAP[currentDayNumber];
        // if current day not exist in availableDaysMap we should not generate data for this day
        if (!availableDaysMap[dayName]) {
            continue;
        }
        const currentDate = currentDay.format('YYYY-MM-DD');
        let slots: IScheduleAvailabilitiesSingleSlotResponse[] = [];
        if (memoizedData[currentDate]) {
            slots = memoizedData[currentDate];
        } else {
            slots = generateSlots(currentDate);
            memoizedData[currentDate] = slots;
        }
        dates.push({ slots, date: currentDate });
    }
    return dates;
};

export const generateSchedulerData = ({
    startDate,
    endDate,
    sessionConfig
}: {
    startDate: string;
    endDate: string;
    sessionConfig: ISessionConfig;
}): IScheduleAvailabilitiesResponse => {
    if (!sessionConfig.day_availabilities) {
        return undefined;
    }
    const availableDays = sessionConfig.day_availabilities.filter(
        dayAvailability => dayAvailability.time_ranges.length > 0
    );

    const data = {
        dates: generateDates({ availableDays, startDate, endDate })
    };

    return data;
};
